import { ArrowRight, Timer, Play } from 'lucide-react';
import { TrustBarCompact } from '../TrustBar';

interface HeroProps {
  onStartFree: () => void;
  onSeeHowItWorks?: () => void;
}

export function Hero({ onStartFree, onSeeHowItWorks }: HeroProps) {
  return (
    <section className="relative bg-gradient-to-b from-[#FDF8F4] to-[#FDFBF7] pt-24 pb-20 overflow-hidden">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 right-1/4 w-96 h-96 bg-coral-200/20 rounded-full blur-3xl" />
        <div className="absolute bottom-20 left-1/4 w-80 h-80 bg-sage-200/20 rounded-full blur-3xl" />
      </div>

      <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div className="text-center lg:text-left order-2 lg:order-1">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-full shadow-sm mb-6">
              <Timer className="h-4 w-4 text-coral-500" />
              <span className="text-sm font-medium text-slate-700">Free forever. No credit card required.</span>
            </div>

            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-[1.1] mb-6">
              Stop overthinking.
              <span className="block text-coral-500">Get a clear plan in minutes.</span>
            </h1>

            <p className="text-lg sm:text-xl text-slate-600 mb-4 leading-relaxed max-w-lg mx-auto lg:mx-0">
              An AI coach that listens, asks the right questions, and turns what's in your head into a plan you can actually follow. Not therapy. Not a chatbot. Something better.
            </p>

            <div className="flex flex-col sm:flex-row gap-3 text-sm text-slate-500 mb-8 justify-center lg:justify-start">
              <span className="flex items-center gap-1.5"><span className="text-coral-500 font-bold">1.</span> Tell your coach what's on your mind</span>
              <span className="hidden sm:block text-slate-300">→</span>
              <span className="flex items-center gap-1.5"><span className="text-coral-500 font-bold">2.</span> Get a personalized response</span>
              <span className="hidden sm:block text-slate-300">→</span>
              <span className="flex items-center gap-1.5"><span className="text-coral-500 font-bold">3.</span> Leave with a concrete next step</span>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-8">
              <button
                onClick={onStartFree}
                className="group inline-flex items-center justify-center px-8 py-4 bg-slate-900 text-white font-semibold text-lg rounded-full hover:bg-slate-800 transition-all duration-200 shadow-lg shadow-slate-900/20 hover:shadow-xl hover:shadow-slate-900/25"
              >
                Start coaching for free
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform duration-200" />
              </button>
              {onSeeHowItWorks && (
                <button
                  onClick={onSeeHowItWorks}
                  className="group inline-flex items-center justify-center px-6 py-4 bg-white text-slate-700 font-medium text-lg rounded-full hover:bg-slate-50 transition-all duration-200 border border-slate-200 hover:border-slate-300"
                >
                  <Play className="mr-2 h-4 w-4 text-coral-500" />
                  See a real conversation
                </button>
              )}
            </div>

            <TrustBarCompact className="mb-6" />

            <p className="text-sm text-slate-500 max-w-lg mx-auto lg:mx-0">
              Not therapy. No judgment. Private by default.
            </p>
          </div>

          <div className="relative order-1 lg:order-2">
            <div className="relative max-w-md mx-auto lg:max-w-none">
              <div className="absolute -inset-4 bg-gradient-to-tr from-coral-100/50 to-sage-100/50 rounded-3xl blur-2xl" />

              <div className="relative">
                <img
                  src="https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=800&h=1000&fit=crop&crop=face"
                  alt="Woman finding clarity and peace"
                  className="relative rounded-3xl shadow-2xl shadow-slate-900/10 w-full aspect-[4/5] object-cover"
                />

                <div className="absolute -bottom-6 -left-6 sm:left-4 bg-white rounded-2xl shadow-xl p-4 max-w-[200px] animate-fade-in-up">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-coral-400 to-coral-500 flex items-center justify-center">
                      <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-slate-900">Coach</p>
                      <p className="text-xs text-slate-500">Just now</p>
                    </div>
                  </div>
                  <p className="text-sm text-slate-600 leading-relaxed">
                    "What would make today feel meaningful to you?"
                  </p>
                </div>

                <div className="absolute -top-4 -right-4 sm:right-4 bg-white rounded-2xl shadow-xl p-4 max-w-[180px] animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-8 h-8 rounded-full bg-sage-100 flex items-center justify-center">
                      <svg className="w-4 h-4 text-sage-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <p className="text-sm font-medium text-slate-900">Progress</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full w-4/5 bg-gradient-to-r from-sage-400 to-sage-500 rounded-full" />
                    </div>
                    <span className="text-xs font-bold text-sage-600">80%</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">Weekly goal</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
